import React, { useCallback, useRef } from 'react'
import { Box, unstable_composeClasses } from '@mui/material'
import classnames from 'classnames'

import { testHandle } from '@acre/utils'

import DisplayField from '../DisplayField/DisplayField'
import type { FieldBaseProps } from '../Field/Field.types'
import FieldWrapper from '../FieldWrapper/FieldWrapper'
import TextFieldBase from '../TextFieldBase'
import type { TextFieldBaseProps } from '../TextFieldBase/TextFieldBase.types'
import { getTextFieldUtilityClass } from './textFieldClasses'
import { useProtectedTextField } from './useProtectedTextField'

export interface TextFieldProps extends FieldBaseProps, Omit<TextFieldBaseProps, 'name' | 'size'> {
  enableProtected?: boolean
  loadData?: () => Promise<string>
}

export interface ProtectedTextFieldProps extends Omit<TextFieldProps, 'inputRef' | 'enableProtected'> {
  id: string
  inputRef: React.RefObject<HTMLInputElement | null>
}

const useUtilityClasses = (ownerState: Partial<TextFieldProps>) => {
  const slots = {
    root: ['root'],
    field: ['field'],
    label: ['label'],
    input: ['input'],
    helperText: ['helperText'],
  }

  return unstable_composeClasses(slots, getTextFieldUtilityClass, ownerState.classes)
}

/**
 * This internal component is used to render the protected text field.
 * It is NOT intended to be used directly, and is only exported for testing purposes.
 */
export const ProtectedTextField = React.memo(function ProtectedTextField(props: ProtectedTextFieldProps) {
  const {
    className,
    classes,
    inputRef,
    value,
    name,
    label,
    loadData,
    onChange,
    WrapperProps,
    variant = 'horizontal',
    size = 'medium',
    ...rootProps
  } = props

  const slotClasses = useUtilityClasses({ classes, variant })

  const initialValue = (value || props.defaultValue) as string

  const testId = rootProps['data-testid'] || props.id || props.name?.replace(/\./g, '-')

  const { isProtected, isEditMode, controlButtons, protectedValue, setProtectedValue } = useProtectedTextField({
    name,
    inputRef,
    initialValue,
    loadData,
    disabled: props.disabled,
    dataTestId: testId,
  })

  const handleChange = useCallback<Required<TextFieldProps>['onChange']>(
    (event) => {
      setProtectedValue(event.target.value)

      if (onChange) {
        onChange(event)
      }
    },
    [onChange, setProtectedValue],
  )

  const isShowMode = !isEditMode && !isProtected

  return (
    <>
      {/* Actual Text Field */}
      {isEditMode && (
        <FieldWrapper
          fieldId={rootProps.id}
          label={label}
          data-testid={testId ? `${testHandle(testId)}-wrapper` : ''}
          {...WrapperProps}
          className={classnames(slotClasses.root, className)}
          labelClassName={classnames(slotClasses.label)}
          variant={variant}
          size={size}
        >
          <TextFieldBase
            {...rootProps}
            className={classnames(slotClasses.field)}
            mode="editable"
            autoComplete="off"
            variant={variant}
            name={name}
            disabled={rootProps.disabled}
            inputRef={inputRef}
            value={protectedValue || ''}
            onChange={handleChange}
          />
        </FieldWrapper>
      )}

      {/* Obfuscated Text Field */}
      {!isEditMode && (
        <Box alignItems="flex-start" display="flex">
          <DisplayField
            name={name}
            disabled={rootProps.disabled}
            value={isShowMode ? (protectedValue as string) : initialValue}
            label={label}
            controls={controlButtons}
            variant={variant}
            size={size}
          />
        </Box>
      )}
    </>
  )
})

/**
 * This is the basic text field component.
 *
 * It used used to power all input types that require keyboard input, such as:
 * - Text
 * - Date
 * - Select
 */
const TextField = (props: TextFieldProps) => {
  const {
    className,
    classes,
    inputRef,
    enableProtected,
    value = '',
    loadData,
    label,
    WrapperProps,
    variant = 'horizontal',
    size = 'medium',
    ...rootProps
  } = props

  const slotClasses = useUtilityClasses({ classes, variant })

  const textFieldRef = useRef<HTMLInputElement | null>((inputRef as React.RefObject<HTMLInputElement>)?.current || null)
  const id = rootProps.id || rootProps.name?.replace(/\./g, '-')
  const labelId = `${id}-label`
  const testId = props['data-testid'] || id

  const commonProps: TextFieldBaseProps = {
    variant,
    InputLabelProps: {
      ...rootProps.InputLabelProps,
      disableAnimation: true,
      shrink: false,
      id: labelId,
    },
    inputProps: {
      ...rootProps.inputProps,
      'aria-labelledby': labelId,
      'data-testid': testId ? testHandle(testId) : '',
      className: classnames(slotClasses.input, rootProps.inputProps?.className),
    },
    FormHelperTextProps: {
      ...rootProps.FormHelperTextProps,
      className: classnames(slotClasses.helperText, rootProps.FormHelperTextProps?.className),
      /* @ts-expect-error - MUI is wrong, props are forwarded */
      ['data-testid']: testId ? testHandle(`${testId}-helperText`) : '',
    },
  }

  if (enableProtected) {
    return (
      <ProtectedTextField
        {...rootProps}
        {...commonProps}
        id={id}
        data-testid={testId}
        label={label}
        value={value}
        loadData={loadData}
        inputRef={textFieldRef}
        WrapperProps={{ ...WrapperProps }}
        size={size}
      />
    )
  }

  return (
    <FieldWrapper
      fieldId={id}
      label={label}
      data-testid={testId ? `${testHandle(testId)}-wrapper` : ''}
      variant={variant}
      size={size}
      {...WrapperProps}
      className={classnames(slotClasses.root, WrapperProps?.className)}
      labelClassName={classnames(slotClasses.label, WrapperProps?.labelClassName)}
    >
      <TextFieldBase
        {...rootProps}
        {...commonProps}
        className={classnames(slotClasses.field, className)}
        id={id}
        value={value}
        autoComplete="off"
        inputRef={textFieldRef}
      />
    </FieldWrapper>
  )
}

export default React.memo(TextField)
